import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { iniciativasAPI, areasAPI, personasAPI, negociosAPI, dedicacionesAPI, hitosFacturacionAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Autocomplete, TextField } from '@mui/material';
import FechaEntregaRealModal from '../components/FechaEntregaRealModal';
import EnlacePropuestaModal from '../components/EnlacePropuestaModal';
import SupervisionModal from '../components/SupervisionModal';
import MotivoRechazoModal from '../components/MotivoRechazoModal';
import AprobadaModal from '../components/AprobadaModal';
import HitoModal from '../components/HitoModal';
import { Widgets, WidthFull } from '@mui/icons-material';

const EditarIniciativa = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const from = searchParams.get('from');
  const { user } = useAuth();
  const currentUserName = user ? (user.full_name || `${user.first_name} ${user.last_name}`.trim() || user.username) : '';
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Modal de fecha de entrega real (al cambiar estado a Entregada)
  const [fechaEntregaRealModalOpen, setFechaEntregaRealModalOpen] = useState(false);
  const fechaEntregaRealRef = useRef(null);

  // Modal de enlace propuesta (al pasar a supervision/entregada sin enlace)
  const [enlaceModalOpen, setEnlaceModalOpen] = useState(false);
  const [enlaceActual, setEnlaceActual] = useState('');
  const enlaceConfirmadoRef = useRef(null);

  // Modal de supervisión (al pasar a entregada sin supervisión)
  const [supervisionModalOpen, setSupervisionModalOpen] = useState(false);
  const [supervisionStatus, setSupervisionStatus] = useState(null);
  const [supervisionLoading, setSupervisionLoading] = useState(false);
  const supervisionConfirmadaRef = useRef(false);

  // Modal de motivo de rechazo (al pasar a rechazada sin motivo)
  const [motivoRechazoModalOpen, setMotivoRechazoModalOpen] = useState(false);
  const motivoRechazoRef = useRef(null);

  // Modal de campos requeridos al aprobar
  const [aprobadaModalOpen, setAprobadaModalOpen] = useState(false);
  const aprobadaDataRef = useRef(null);
  const [areas, setAreas] = useState([]);
  const [negocios, setNegocios] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [iniciativa, setIniciativa] = useState(null);
  const [iniciativas, setIniciativas] = useState([]);
  const [activeTab, setActiveTab] = useState('datos-basicos');
  const [activeSubTab, setActiveSubTab] = useState('general');

  // Estados para las secciones relacionadas
  const [personasVinculadas, setPersonasVinculadas] = useState([]);
  const [hitosFacturacion, setHitosFacturacion] = useState([]);
  const [comentarios, setComentarios] = useState([]);

  // Modal de edición de hito
  const [hitoModalOpen, setHitoModalOpen] = useState(false);
  const [hitoSeleccionado, setHitoSeleccionado] = useState(null);
  const [nuevoComentario, setNuevoComentario] = useState('');
  const [editingComentarioId, setEditingComentarioId] = useState(null);
  const [editingComentarioTexto, setEditingComentarioTexto] = useState('');
  const [selectedPersona, setSelectedPersona] = useState('');
  const [selectedRol, setSelectedRol] = useState('');
  const [subnegocioOptions, setSubnegocioOptions] = useState([]);
  const [zonaGeograficaOptions, setZonaGeograficaOptions] = useState([]);
  const [paisOptions, setPaisOptions] = useState([]);

  useEffect(() => {
    negociosAPI.getAll().then(res => {
      const negocios = res.data.results || res.data || [];
      const unique = [...new Set(negocios.map(n => n.subnegocio).filter(Boolean))]
        .sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));
      setSubnegocioOptions(unique);
    }).catch(err => console.error('Error cargando subnegocio opciones:', err));

    hitosFacturacionAPI.getChoices().then(res => {
      setZonaGeograficaOptions(res.data.zona_geografica || []);
      setPaisOptions(res.data.pais || []);
    }).catch(err => console.error('Error cargando choices hitos:', err));
  }, []);

  const [nuevoHito, setNuevoHito] = useState({
    numero_hito: '',
    nombre: '',
    fecha: '',
    porcentaje: '',
    importe: '',
    cliente: '',
    sociedad_final: '',
    zona_geografica: '',
    pais: '',
    facturado: false,
    fecha_facturacion: '',
    observaciones: ''
  });

  const [formData, setFormData] = useState({
    nombre_de_la_oportunidad: '',
    descripcion: '',
    negocio_estructura: '',
    contacto_de_negocio: '',
    estado: '',
    tecnologia: '',
    area_ejecucion: '',
    valor_minimo_estimado: '',
    valor_maximo_estimado: '',
    duracion_minima_estimada: '',
    duracion_maxima_estimada: '',
    fecha_de_entrega_propuesta_prevista: '',
    fecha_de_fin_planificada: '',
    año: '',
    madurez: '',
    revision_dn: false,
    tipo_de_iniciativa: 'iniciativa',
    relevancia_para_el_dh: false,
    productivizado: false,
    enlace_al_documento_ppt_sharepoint: '',
    enlace_a_la_pagina_de_confluence: '',
    importe_total_estimado: '',
    importe_total_cliente: '',
    probabilidad_de_aprobacion: '',
    revision_lt: false,
    fecha_de_aprobacion_propuesta_firmada: '',
    porcentaje_avance: '',
    coste_de_desarrollo_estimado: '',
    relevancia_para_negocio: '',
    innovacion: false,
    tipo_innovacion: '',
    en_uso_escalado: false,
    propuesta_enviada: false,
    enlace_propuesta_pdf_sharepoint: '',
    motivo_tipificado: '',
    fecha_de_inicio_planificada: '',
    fecha_de_inicio_real: '',
    fecha_de_fin_real: '',
    coste_de_gastos_estimado: '',
    hardware_estimado: '',
    opex_estimado: '',
    coste_de_alquiler_de_equipos_estimado: '',
    riesgos_estimado: '',
    estructura_estimado: '',
    // Campos reales
    coste_de_desarrollo_real: '',
    coste_de_gastos_real: '',
    hardware_real: '',
    opex_real: '',
    coste_de_alquiler_de_equipos_real: '',
    riesgos_real: '',
    estructura_real: '',
    // Campos de desviación
    porcentaje_avance_esperado: '',
    desviacion_tiempo: '',
    coste_esperado: '',
    desviacion_coste: '',
    horas_desarrollo_estimado: '',
    costes_reales: '',
    fecha_ultima_actualizacion_azuredevops: '',
    // Campos adicionales
    probabilidad_de_aprobacion: '',
    padre_id: ''
  });

  useEffect(() => {
    cargarDatos();
  }, [id]);

  // Función para formatear fechas para datetime-local input
  const formatDateTimeForInput = (dateTimeString) => {
    if (!dateTimeString) return '';
    try {
      const date = new Date(dateTimeString);
      if (isNaN(date.getTime())) return '';
      // Formatear como YYYY-MM-DDTHH:MM para datetime-local input
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch (error) {
      console.warn('Error formateando fecha:', dateTimeString, error);
      return '';
    }
  };

  const cargarDatos = async () => {
    try {
      setLoading(true);

      // Cargar datos de referencia y la iniciativa
      const [areasResponse, personasResponse, negociosResponse, iniciativasResponse] = await Promise.all([
        areasAPI.getAll(),
        personasAPI.getAll(),
        negociosAPI.getAll(),
        iniciativasAPI.getAll()
      ]);

      const areasData = Array.isArray(areasResponse.data) ? areasResponse.data : areasResponse.data?.results || [];
      const personasData = Array.isArray(personasResponse.data) ? personasResponse.data : personasResponse.data?.results || [];
      const negociosData = Array.isArray(negociosResponse.data) ? negociosResponse.data : negociosResponse.data?.results || [];
      const todasIniciativas = Array.isArray(iniciativasResponse.data) ? iniciativasResponse.data : iniciativasResponse.data?.results || [];

      setAreas(areasData);
      setPersonas(personasData);
      setNegocios(negociosData);
      setIniciativas(todasIniciativas.filter(i => i.id !== parseInt(id)));

      // Debug: Verificar datos de referencia
      console.log('Datos de referencia cargados:');
      console.log('Areas:', areasData.length, 'items');
      console.log('Personas:', personasData.length, 'items');
      console.log('Negocios:', negociosData.length, 'items');
      if (areasData.length > 0) console.log('Primera área:', areasData[0]);
      if (negociosData.length > 0) console.log('Primer negocio:', negociosData[0]);

      // Cargar la iniciativa específica
      const iniciativaResponse = await iniciativasAPI.getById(id);
      const iniciativaData = iniciativaResponse.data;
      setIniciativa(iniciativaData);

      // Debug: Verificar datos de campos relacionados
      console.log('Datos de campos relacionados desde API:');
      console.log('tecnologia:', iniciativaData.tecnologia);
      console.log('area_ejecucion:', iniciativaData.area_ejecucion);
      console.log('negocio_estructura:', iniciativaData.negocio_estructura);
      console.log('tecnologia_id:', iniciativaData.tecnologia_id);
      console.log('area_ejecucion_id:', iniciativaData.area_ejecucion_id);
      console.log('negocio_estructura_id:', iniciativaData.negocio_estructura_id);

      setFormData({
        ...iniciativaData,
        descripcion: iniciativaData.descripcion || '',
        relevancia_para_negocio: iniciativaData.relevancia_para_negocio || '',
        contacto_de_negocio: iniciativaData.contacto_de_negocio || '',
        tecnologia: iniciativaData.tecnologia || '',
        area_ejecucion: iniciativaData.area_ejecucion || '',
        negocio_estructura: iniciativaData.negocio_estructura || '',
        valor_minimo_estimado: iniciativaData.valor_minimo_estimado || '',
        valor_maximo_estimado: iniciativaData.valor_maximo_estimado || '',
        importe_total_estimado: iniciativaData.importe_total_estimado || '',
        importe_total_cliente: iniciativaData.importe_total_cliente || '',
        coste_de_desarrollo_estimado: iniciativaData.coste_de_desarrollo_estimado || '',
        importe_total_real: iniciativaData.importe_total_real || '',
        porcentaje_avance: iniciativaData.porcentaje_avance || '',
        duracion_minima_estimada: iniciativaData.duracion_minima_estimada || '',
        duracion_maxima_estimada: iniciativaData.duracion_maxima_estimada || '',
        tipo_de_iniciativa: iniciativaData.tipo_de_iniciativa || 'iniciativa',
        tipo_innovacion: iniciativaData.tipo_innovacion || '',
        propuesta_enviada: iniciativaData.propuesta_enviada || false,
        enlace_propuesta_pdf_sharepoint: iniciativaData.enlace_propuesta_pdf_sharepoint || '',
        motivo_tipificado: iniciativaData.motivo_tipificado || '',
        fecha_de_inicio_planificada: iniciativaData.fecha_de_inicio_planificada || '',
        fecha_de_inicio_real: iniciativaData.fecha_de_inicio_real || '',
        fecha_de_fin_real: iniciativaData.fecha_de_fin_real || '',
        coste_de_gastos_estimado: iniciativaData.coste_de_gastos_estimado || '',
        hardware_estimado: iniciativaData.hardware_estimado || '',
        opex_estimado: iniciativaData.opex_estimado || '',
        coste_de_alquiler_de_equipos_estimado: iniciativaData.coste_de_alquiler_de_equipos_estimado || '',
        riesgos_estimado: iniciativaData.riesgos_estimado || '',
        estructura_estimado: iniciativaData.estructura_estimado || '',
        fecha_de_entrega_propuesta_prevista: iniciativaData.fecha_de_entrega_propuesta_prevista || '',
        fecha_de_fin_planificada: iniciativaData.fecha_de_fin_planificada || '',
        fecha_de_aprobacion_propuesta_firmada: iniciativaData.fecha_de_aprobacion_propuesta_firmada || '',
        // Campos de costes reales
        coste_de_desarrollo_real: iniciativaData.coste_de_desarrollo_real || '',
        coste_de_gastos_real: iniciativaData.coste_de_gastos_real || '',
        hardware_real: iniciativaData.hardware_real || '',
        opex_real: iniciativaData.opex_real || '',
        coste_de_alquiler_de_equipos_real: iniciativaData.coste_de_alquiler_de_equipos_real || '',
        riesgos_real: iniciativaData.riesgos_real || '',
        estructura_real: iniciativaData.estructura_real || '',
        // Campos de desviación
        porcentaje_avance_esperado: iniciativaData.porcentaje_avance_esperado || '',
        desviacion_tiempo: iniciativaData.desviacion_tiempo || '',
        coste_esperado: iniciativaData.coste_esperado || '',
        desviacion_coste: iniciativaData.desviacion_coste || '',
        horas_desarrollo_estimado: iniciativaData.horas_desarrollo_estimado || '',
        costes_reales: iniciativaData.costes_reales || '',
        fecha_ultima_actualizacion_azuredevops: formatDateTimeForInput(iniciativaData.fecha_ultima_actualizacion_azuredevops),
        // Campos adicionales que podrían estar faltando
        año: iniciativaData.año || '',
        madurez: iniciativaData.madurez || '',
        revision_dn: iniciativaData.revision_dn || false,
        revision_lt: iniciativaData.revision_lt || false,
        relevancia_para_el_dh: iniciativaData.relevancia_para_el_dh || false,
        productivizado: iniciativaData.productivizado || false,
        enlace_al_documento_ppt_sharepoint: iniciativaData.enlace_al_documento_ppt_sharepoint || '',
        enlace_a_la_pagina_de_confluence: iniciativaData.enlace_a_la_pagina_de_confluence || '',
        innovacion: iniciativaData.innovacion || false,
        en_uso_escalado: iniciativaData.en_uso_escalado || false,
        id_proyecto: iniciativaData.id_proyecto || '',
        probabilidad_de_aprobacion: iniciativaData.probabilidad_de_aprobacion || '',
        estado: iniciativaData.estado || '',
        padre_id: iniciativaData.padre || ''
      });

      // Debug: Verificar valores mapeados del formulario
      const formValues = {
        tecnologia: iniciativaData.tecnologia ? iniciativaData.tecnologia.id : (iniciativaData.tecnologia_id || ''),
        area_ejecucion: iniciativaData.area_ejecucion ? iniciativaData.area_ejecucion.id : (iniciativaData.area_ejecucion_id || ''),
        negocio_estructura: iniciativaData.negocio_estructura ? iniciativaData.negocio_estructura.id : (iniciativaData.negocio_estructura_id || '')
      };
      console.log('Valores mapeados para el formulario:');
      console.log('tecnologia valor:', formValues.tecnologia);
      console.log('area_ejecucion valor:', formValues.area_ejecucion);
      console.log('negocio_estructura valor:', formValues.negocio_estructura);

      // Cargar personas vinculadas usando endpoint específico
      try {
        const personasVinculadasResponse = await iniciativasAPI.getPersonas(id);
        setPersonasVinculadas(personasVinculadasResponse.data || []);
      } catch (personasErr) {
        console.error('Error cargando personas vinculadas:', personasErr);
        setPersonasVinculadas(iniciativaData.personas || []);
      }

      // Cargar hitos de facturación
      setHitosFacturacion(iniciativaData.hitos_facturacion || []);

      // Cargar comentarios
      setComentarios(iniciativaData.comentarios || []);

    } catch (err) {
      console.error('Error cargando datos:', err);
      setError('Error al cargar los datos de la iniciativa');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Convierte los errores de validación de la API (DRF) a mensajes legibles en español
  const parsearErroresAPI = (err) => {
    const etiquetas = {
      nombre_de_la_oportunidad: 'Nombre de la oportunidad',
      descripcion: 'Descripción',
      tipo_de_iniciativa: 'Tipo',
      negocio_estructura: 'Subnegocio / Negocio estructura',
      contacto_de_negocio: 'Contacto de negocio',
      tecnologia: 'Tecnología',
      area_ejecucion: 'Área de ejecución',
      estado: 'Estado',
      fecha_de_entrega_propuesta_prevista: 'Fecha de entrega propuesta prevista',
      fecha_de_inicio_planificada: 'Fecha de inicio planificada',
      fecha_de_inicio_real: 'Fecha de inicio real',
      fecha_de_fin_planificada: 'Fecha de fin planificada',
      fecha_de_fin_real: 'Fecha de fin real',
      fecha_de_aprobacion_propuesta_firmada: 'Fecha de aprobación propuesta firmada',
      fecha_de_entrega_propuesta_real: 'Fecha de entrega real',
      fecha_ultima_actualizacion_azuredevops: 'Última actualización Azure DevOps',
      valor_minimo_estimado: 'Valor mínimo estimado',
      valor_maximo_estimado: 'Valor máximo estimado',
      duracion_minima_estimada: 'Duración mínima estimada',
      duracion_maxima_estimada: 'Duración máxima estimada',
      importe_total_estimado: 'Importe total estimado',
      importe_total_cliente: 'Importe total cliente',
      coste_de_desarrollo_estimado: 'Coste de desarrollo estimado',
      coste_de_gastos_estimado: 'Coste de gastos estimado',
      hardware_estimado: 'Hardware estimado',
      opex_estimado: 'OPEX estimado',
      coste_de_alquiler_de_equipos_estimado: 'Coste de alquiler de equipos estimado',
      riesgos_estimado: 'Riesgos estimado',
      estructura_estimado: 'Estructura estimado',
      coste_de_desarrollo_real: 'Coste de desarrollo real',
      coste_de_gastos_real: 'Coste de gastos real',
      hardware_real: 'Hardware real',
      opex_real: 'OPEX real',
      coste_de_alquiler_de_equipos_real: 'Coste de alquiler de equipos real',
      riesgos_real: 'Riesgos real',
      estructura_real: 'Estructura real',
      enlace_al_documento_ppt_sharepoint: 'Enlace PPT SharePoint',
      enlace_a_la_pagina_de_confluence: 'Enlace Confluence',
      enlace_propuesta_pdf_sharepoint: 'Enlace propuesta PDF SharePoint',
      motivo_tipificado: 'Motivo de rechazo',
      probabilidad_de_aprobacion: 'Probabilidad de aprobación',
      porcentaje_avance: 'Porcentaje de avance',
      año: 'Año',
      madurez: 'Madurez',
      innovacion: 'Innovación',
      tipo_innovacion: 'Tipo de innovación',
      horas_desarrollo_estimado: 'Horas de desarrollo estimadas',
      padre: 'Iniciativa padre',
      padre_id: 'Iniciativa padre',
    };
    const data = err?.response?.data;
    if (!data || typeof data !== 'object') {
      return 'Error al guardar. Por favor, revise los datos e intente nuevamente.';
    }
    const mensajes = [];
    Object.entries(data).forEach(([campo, errores]) => {
      const etiqueta = etiquetas[campo] || campo;
      const lista = Array.isArray(errores) ? errores : [errores];
      lista.forEach(msg => {
        const msgStr = typeof msg === 'string' ? msg : JSON.stringify(msg);
        if (/blank|null|vacío|required|obligatorio/i.test(msgStr)) {
          mensajes.push(`El campo "${etiqueta}" es obligatorio y no puede estar vacío.`);
        } else {
          mensajes.push(`${etiqueta}: ${msgStr}`);
        }
      });
    });
    return mensajes.length > 0
      ? mensajes.join('\n')
      : 'Error al guardar. Por favor, revise los datos e intente nuevamente.';
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    // Verificar motivo para estado rechazada
    if (formData.estado === 'rechazada' && !formData.motivo_tipificado && !motivoRechazoRef.current) {
      setMotivoRechazoModalOpen(true);
      return;
    }

    // Verificar enlace para estados supervision, entregada o aprobado
    if ((formData.estado === 'supervision' || formData.estado === 'entregada' || formData.estado === 'aprobado')
      && !formData.enlace_al_documento_ppt_sharepoint
      && !enlaceConfirmadoRef.current) {
      try {
        const enlaceResponse = await iniciativasAPI.checkEnlacePropuesta(id);
        if (!enlaceResponse.data.tiene_enlace) {
          setEnlaceActual(enlaceResponse.data.enlace_al_documento_ppt_sharepoint || '');
          setEnlaceModalOpen(true);
          return;
        }
      } catch (err) {
        console.error('Error al verificar enlace:', err);
      }
    }

    // Verificar supervisión para estados entregada o aprobado
    if ((formData.estado === 'entregada' || formData.estado === 'aprobado') && !supervisionConfirmadaRef.current) {
      try {
        const response = await iniciativasAPI.checkSupervision(id);
        if (!response.data.can_move_to_entregada) {
          setSupervisionStatus(response.data);
          setSupervisionModalOpen(true);
          return;
        }
      } catch (err) {
        console.error('Error al verificar supervisión:', err);
      }
    }

    // Si el estado es Entregada o Aprobado y no hay fecha de entrega real, mostrar modal de fecha
    if ((formData.estado === 'entregada' || formData.estado === 'aprobado')
      && !formData.fecha_de_entrega_propuesta_real
      && !fechaEntregaRealRef.current) {
      setFechaEntregaRealModalOpen(true);
      return;
    }

    // Si el estado cambia a Aprobado, verificar campos de aprobación
    if (formData.estado === 'aprobado' && !aprobadaDataRef.current) {
      const faltaImporte = !formData.importe_total_cliente;
      const faltaFechaAprobacion = !formData.fecha_de_aprobacion_propuesta_firmada;
      if (faltaImporte || faltaFechaAprobacion) {
        setAprobadaModalOpen(true);
        return;
      }
    }

    try {
      setLoading(true);
      setError(null);

      // Preparar datos para envío
      const dataToSend = {
        ...formData,
        // Convertir strings vacíos a null para campos numéricos opcionales
        valor_minimo_estimado: formData.valor_minimo_estimado || null,
        valor_maximo_estimado: formData.valor_maximo_estimado || null,
        // Para campos numéricos requeridos, usar 0 si está vacío
        duracion_minima_estimada: formData.duracion_minima_estimada ? parseInt(formData.duracion_minima_estimada) : 0,
        duracion_maxima_estimada: formData.duracion_maxima_estimada ? parseInt(formData.duracion_maxima_estimada) : 0,
        importe_total_estimado: formData.importe_total_estimado || null,
        importe_total_cliente: aprobadaDataRef.current?.importe_total_cliente ?? (formData.importe_total_cliente || null),
        coste_de_desarrollo_estimado: formData.coste_de_desarrollo_estimado || null,
        importe_total_real: formData.importe_total_real || null,
        coste_de_gastos_estimado: formData.coste_de_gastos_estimado || null,
        hardware_estimado: formData.hardware_estimado || null,
        opex_estimado: formData.opex_estimado || null,
        coste_de_alquiler_de_equipos_estimado: formData.coste_de_alquiler_de_equipos_estimado || null,
        riesgos_estimado: formData.riesgos_estimado || null,
        estructura_estimado: formData.estructura_estimado || null,
        enlace_propuesta_pdf_sharepoint: formData.enlace_propuesta_pdf_sharepoint || '',
        motivo_tipificado: motivoRechazoRef.current || formData.motivo_tipificado || '',
        tipo_innovacion: formData.tipo_innovacion || null,
        fecha_de_inicio_planificada: formData.fecha_de_inicio_planificada || null,
        fecha_de_inicio_real: formData.fecha_de_inicio_real || null,
        fecha_de_fin_real: formData.fecha_de_fin_real || null,
        porcentaje_avance: formData.porcentaje_avance ? parseFloat(formData.porcentaje_avance) : null,
        tecnologia: formData.tecnologia || null,
        area_ejecucion: formData.area_ejecucion || null,
        negocio_estructura: formData.negocio_estructura || null,
        // Asegurar que tipo_de_iniciativa tenga un valor válido
        tipo_de_iniciativa: formData.tipo_de_iniciativa || 'iniciativa',
        // Fechas vacías se convierten a null
        fecha_de_entrega_propuesta_prevista: formData.fecha_de_entrega_propuesta_prevista || null,
        fecha_de_fin_planificada: formData.fecha_de_fin_planificada || null,
        fecha_de_aprobacion_propuesta_firmada: aprobadaDataRef.current?.fecha_de_aprobacion_propuesta_firmada ?? (formData.fecha_de_aprobacion_propuesta_firmada || null),
        año: formData.año ? parseInt(formData.año) : null,
        madurez: formData.madurez || null,
        // Campos de desviación y costes adicionales
        porcentaje_avance_esperado: formData.porcentaje_avance_esperado ? parseFloat(formData.porcentaje_avance_esperado) : null,
        desviacion_tiempo: formData.desviacion_tiempo ? parseFloat(formData.desviacion_tiempo) : null,
        coste_esperado: formData.coste_esperado ? parseFloat(formData.coste_esperado) : null,
        desviacion_coste: formData.desviacion_coste ? parseFloat(formData.desviacion_coste) : null,
        horas_desarrollo_estimado: formData.horas_desarrollo_estimado ? parseFloat(formData.horas_desarrollo_estimado) : null,
        costes_reales: formData.costes_reales ? parseFloat(formData.costes_reales) : null,
        coste_de_desarrollo_real: formData.coste_de_desarrollo_real ? parseFloat(formData.coste_de_desarrollo_real) : null,
        // Formatear fecha datetime para envío a la API
        fecha_ultima_actualizacion_azuredevops: formData.fecha_ultima_actualizacion_azuredevops ? new Date(formData.fecha_ultima_actualizacion_azuredevops).toISOString() : null,
        // Corregir manejo de probabilidad_de_aprobacion
        probabilidad_de_aprobacion: formData.probabilidad_de_aprobacion && formData.probabilidad_de_aprobacion !== '' && formData.probabilidad_de_aprobacion !== 'No determinada' ? formData.probabilidad_de_aprobacion : null,
        padre: formData.padre_id || null,
        fecha_de_entrega_propuesta_real: fechaEntregaRealRef.current || formData.fecha_de_entrega_propuesta_real || null
      };

      // Actualizar la iniciativa
      await iniciativasAPI.update(id, dataToSend);
      fechaEntregaRealRef.current = null;
      enlaceConfirmadoRef.current = null;
      supervisionConfirmadaRef.current = false;
      motivoRechazoRef.current = null;
      aprobadaDataRef.current = null;

      setSuccess(true);

      // Redireccionar después de un tiempo
      setTimeout(() => {
        navigate(`/projects/${id}${from ? `?from=${from}` : ''}`);
      }, 1500);

    } catch (err) {
      console.error('Error actualizando iniciativa:', err);
      setError(parsearErroresAPI(err));
    } finally {
      setLoading(false);
    }
  };

  const handleAgregarPersona = async (personaId, rol) => {
    try {
      await iniciativasAPI.agregarPersona(id, {
        persona_id: personaId,
        rol: rol
      });

      // Recargar personas vinculadas
      const iniciativaResponse = await iniciativasAPI.getById(id);
      setPersonasVinculadas(iniciativaResponse.data.personas || []);

    } catch (err) {
      console.error('Error agregando persona:', err);
      setError('Error al agregar la persona');
    }
  };

  const handleEliminarPersona = async (personaVinculadaId) => {
    try {
      await iniciativasAPI.eliminarPersona(id, personaVinculadaId);

      // Actualizar la lista local
      setPersonasVinculadas(prev => prev.filter(p => p.id !== personaVinculadaId));

    } catch (err) {
      console.error('Error eliminando persona:', err);
      setError('Error al eliminar la persona');
    }
  };

  const addPersonaToList = async () => {
    if (!selectedPersona || !selectedRol) {
      setError('Debe seleccionar una persona y un rol');
      return;
    }

    const existingPersona = personasVinculadas.find(
      p => p.persona === parseInt(selectedPersona) && p.rol === selectedRol
    );

    if (existingPersona) {
      setError('Esta persona ya tiene asignado ese rol');
      return;
    }

    try {
      await iniciativasAPI.agregarPersona(id, {
        persona_id: parseInt(selectedPersona),
        rol: selectedRol
      });

      // Recargar personas vinculadas usando endpoint específico
      try {
        const personasVinculadasResponse = await iniciativasAPI.getPersonas(id);
        setPersonasVinculadas(personasVinculadasResponse.data || []);
      } catch (reloadErr) {
        console.error('Error recargando personas:', reloadErr);
        // Fallback: recargar toda la iniciativa
        const iniciativaResponse = await iniciativasAPI.getById(id);
        setPersonasVinculadas(iniciativaResponse.data.personas || []);
      }

      setSelectedPersona('');
      setSelectedRol('');
      setError(null);
    } catch (err) {
      console.error('Error adding person:', err);
      setError('Error al agregar la persona a la iniciativa');
    }
  };

  const getPersonaById = (personaId) => {
    return personas.find(p => p.id === personaId) || {};
  };

  const getRolDisplay = (rol) => {
    const roles = {
      jefe_proyecto: 'Jefe de Proyecto',
      lider_tecnico: 'Líder Técnico',
      desarrollador: 'Desarrollador',
      responsable_comercial: 'Responsable DN',
      colaborador: 'Colaborador'
    };
    return roles[rol] || rol;
  };

  const handleAgregarComentario = async () => {
    if (!nuevoComentario.trim()) return;

    try {
      await iniciativasAPI.agregarComentario(id, {
        contenido: nuevoComentario
      });

      setNuevoComentario('');

      // Recargar comentarios
      const iniciativaResponse = await iniciativasAPI.getById(id);
      setComentarios(iniciativaResponse.data.comentarios || []);

    } catch (err) {
      console.error('Error agregando comentario:', err);
      setError('Error al agregar el comentario');
    }
  };

  const handleEliminarComentario = async (comentarioId) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este comentario?')) return;
    try {
      await iniciativasAPI.eliminarComentario(id, comentarioId);
      setComentarios(comentarios.filter(c => c.id !== comentarioId));
    } catch (err) {
      console.error('Error eliminando comentario:', err);
      setError('Error al eliminar el comentario');
    }
  };

  const handleGuardarEdicionComentario = async (comentarioId) => {
    if (!editingComentarioTexto.trim()) return;
    try {
      const res = await iniciativasAPI.actualizarComentario(id, comentarioId, { contenido: editingComentarioTexto });
      setComentarios(comentarios.map(c => c.id === comentarioId ? res.data : c));
      setEditingComentarioId(null);
      setEditingComentarioTexto('');
    } catch (err) {
      console.error('Error actualizando comentario:', err);
      setError('Error al actualizar el comentario');
    }
  };

  const handleAgregarHito = async () => {
    try {
      await iniciativasAPI.agregarHitoFacturacion(id, {
        ...nuevoHito,
        porcentaje: nuevoHito.porcentaje || null,
        importe: nuevoHito.importe || null
      });

      // Reset form
      setNuevoHito({
        numero_hito: '',
        nombre: '',
        fecha: '',
        porcentaje: '',
        importe: '',
        cliente: '',
        sociedad_final: '',
        zona_geografica: '',
        pais: '',
        facturado: false,
        fecha_facturacion: '',
        observaciones: ''
      });

      // Recargar hitos
      const iniciativaResponse = await iniciativasAPI.getById(id);
      setHitosFacturacion(iniciativaResponse.data.hitos_facturacion || []);

    } catch (err) {
      console.error('Error agregando hito:', err);
      setError('Error al agregar el hito de facturación');
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const handleAbrirHitoModal = (hito = null) => {
    const negocioActual = negocios.find(n => n.id === Number(formData.negocio_estructura));
    const sociedadFinalFallback = negocioActual?.nombre_sociedad_final || '';
    // Asegurar que el campo iniciativa siempre venga relleno con la iniciativa actual
    // y que sociedad_final tenga valor si el hito lo tenía vacío en BD
    setHitoSeleccionado(hito ? {
      ...hito,
      iniciativa: hito.iniciativa || iniciativa?.id,
      sociedad_final: hito.sociedad_final || sociedadFinalFallback
    } : null);
    setHitoModalOpen(true);
  };

  const handleCerrarHitoModal = () => {
    setHitoModalOpen(false);
    setHitoSeleccionado(null);
  };

  const handleGuardarHitoModal = async () => {
    const iniciativaResponse = await iniciativasAPI.getById(id);
    setHitosFacturacion(iniciativaResponse.data.hitos_facturacion || []);
    handleCerrarHitoModal();
  };



  const handleFechaEntregaRealConfirm = (fecha) => {
    setFechaEntregaRealModalOpen(false);
    fechaEntregaRealRef.current = fecha;
    handleSubmit(null);
  };

  const handleFechaEntregaRealClose = () => {
    setFechaEntregaRealModalOpen(false);
  };

  const handleEnlaceConfirm = (enlace) => {
    enlaceConfirmadoRef.current = enlace;
    setFormData(prev => ({ ...prev, enlace_al_documento_ppt_sharepoint: enlace }));
    setEnlaceModalOpen(false);
    handleSubmit(null);
  };

  const handleEnlaceClose = () => {
    setEnlaceModalOpen(false);
  };

  const handleSupervisionConfirm = async (supervisionData) => {
    setSupervisionLoading(true);
    try {
      await iniciativasAPI.marcarSupervision(id, supervisionData);
      supervisionConfirmadaRef.current = true;
      setSupervisionModalOpen(false);
      setSupervisionStatus(null);
      handleSubmit(null);
    } catch (err) {
      console.error('Error al marcar supervisión:', err);
      setError('Error al registrar la supervisión');
    } finally {
      setSupervisionLoading(false);
    }
  };

  const handleSupervisionClose = () => {
    setSupervisionModalOpen(false);
    setSupervisionStatus(null);
  };

  const handleMotivoRechazoConfirm = (motivo) => {
    motivoRechazoRef.current = motivo;
    setFormData(prev => ({ ...prev, motivo_tipificado: motivo }));
    setMotivoRechazoModalOpen(false);
    handleSubmit(null);
  };

  const handleMotivoRechazoClose = () => {
    setMotivoRechazoModalOpen(false);
  };

  const handleAprobadaConfirm = (data) => {
    aprobadaDataRef.current = data;
    setAprobadaModalOpen(false);
    handleSubmit(null);
  };

  const handleAprobadaClose = () => {
    setAprobadaModalOpen(false);
  };

  if (loading && !iniciativa) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="container mt-4">
        <div className="row justify-content-center">
          <div className="col-md-6">
            <div className="alert alert-success text-center" role="alert">
              <i className="fas fa-check-circle fa-3x mb-3"></i>
              <h4 className="alert-heading">¡Iniciativa actualizada exitosamente!</h4>
              <p>Los cambios han sido guardados en el sistema.</p>
              <hr />
              <p className="mb-0">Redirigiendo al detalle de la iniciativa...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-4">
      <div className="row">
        <div className="col-12">
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2 className="mb-0">
                <i className="fas fa-edit text-primary me-2" style={{ width: 'auto' }}></i>
                Editar Iniciativa
              </h2>
              <p className="text-muted mb-0">
                {iniciativa?.nombre_de_la_oportunidad || 'Cargando...'}
              </p>
            </div>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={handleCancel}
            >
              <i className="fas fa-arrow-left me-2"></i>
              Volver
            </button>
          </div>

          {/* Mensajes de error */}
          {error && (
            <div className="alert alert-danger" role="alert">
              <i className="fas fa-exclamation-triangle me-2"></i>
              {error.split('\n').map((linea, i) => (
                <div key={i}>{linea}</div>
              ))}
            </div>
          )}

          {/* Tabs de navegación */}
          <div className="mb-4">
            <ul className="nav nav-tabs" role="tablist">
              {[
                { key: 'datos-basicos', icon: 'fa-file-alt', label: 'Datos' },
                { key: 'personas', icon: 'fa-users', label: `Personas (${personasVinculadas.length})` },
                { key: 'hitos', icon: 'fa-euro-sign', label: `Hitos (${hitosFacturacion.length})` },
                { key: 'comentarios', icon: 'fa-comments', label: `Comentarios (${comentarios.length})` },
              ].map(({ key, icon, label }) => (
                <li key={key} className="nav-item" role="presentation">
                  <button
                    type="button"
                    className={`nav-link ${activeTab === key ? 'active' : ''}`}
                    onClick={() => setActiveTab(key)}
                    style={activeTab === key
                      ? { color: '#1a1a2e', borderBottomColor: '#1a1a2e', fontWeight: '600' }
                      : { color: '#9ca3af' }
                    }
                  >
                    <i className={`fas ${icon} me-2`}></i>{label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contenido de las tabs */}
          <div className="tab-content">

            {/* Tab de Datos Básicos */}
            {activeTab === 'datos-basicos' && (
              <div className="card shadow-sm border-0">
                <div className="card-body p-0">
                  <form onSubmit={handleSubmit}>

                    {/* Sub-tabs de navegación interna */}
                    <div className="border-bottom bg-light px-4 pt-3">
                      <ul className="nav nav-pills gap-1 pb-3" role="tablist">
                        {[
                          { key: 'general', icon: 'fa-info-circle', label: 'General' },
                          { key: 'economico', icon: 'fa-euro-sign', label: 'Económico' },
                          { key: 'planificacion', icon: 'fa-calendar-alt', label: 'Planificación' },
                          { key: 'documentos', icon: 'fa-link', label: 'Documentos' },
                        ].map(({ key, icon, label }) => (
                          <li key={key} className="nav-item" role="presentation">
                            <button
                              type="button"
                              className={`nav-link ${activeSubTab === key ? 'active' : ''}`}
                              onClick={() => setActiveSubTab(key)}
                              style={activeSubTab === key
                                ? { fontSize: '0.875rem', backgroundColor: '#2d3748', borderColor: '#2d3748', color: '#fff' }
                                : { fontSize: '0.875rem', color: '#9ca3af', backgroundColor: 'transparent' }
                              }
                            >
                              <i className={`fas ${icon} me-2`}></i>{label}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="p-4">

                      {/* ── GENERAL ── */}
                      {activeSubTab === 'general' && (
                        <div className="row g-3">
                          <div className="col-12">
                            <h6 className="text-primary border-bottom pb-2 mb-3">
                              <i className="fas fa-info-circle me-2"></i>Información Básica
                            </h6>
                          </div>

                          <div className="col-lg-8 col-md-12">
                            <label className="form-label">Nombre de la oportunidad *</label>
                            <input
                              type="text"
                              className="form-control"
                              name="nombre_de_la_oportunidad"
                              value={formData.nombre_de_la_oportunidad || ''}
                              onChange={handleInputChange}
                              required
                            />
                          </div>

                          <div className="col-lg-4 col-md-6">
                            <label className="form-label">Tipo de iniciativa</label>
                            <select className="form-control" name="tipo_de_iniciativa" value={formData.tipo_de_iniciativa || ''} onChange={handleInputChange}>
                              <option value="iniciativa">Iniciativa</option>
                              <option value="servicio">Servicio</option>
                              <option value="generica">Genérica</option>
                              <option value="mantenimiento">Mantenimiento</option>
                              <option value="programa">Programa</option>
                            </select>
                          </div>

                          <div className="col-lg-4 col-md-6">
                            <label className="form-label">Estado</label>
                            <select className="form-control" name="estado" value={formData.estado || ''} onChange={handleInputChange}>
                              <option value="oportunidad_identificada">Oportunidad identificada</option>
                              <option value="en_preparacion_propuesta">En preparación propuesta</option>
                              <option value="supervision">Supervisión</option>
                              <option value="entregada">Entregada</option>
                              <option value="rechazada">Rechazada</option>
                              <option value="stand_by">Stand-By</option>
                              <option value="aprobado">Aprobado</option>
                              <option value="planificada">Planificada</option>
                              <option value="en_curso">En curso</option>
                              <option value="bloqueada">Bloqueada</option>
                              <option value="cancelada">Cancelada</option>
                              <option value="finalizada">Finalizada</option>
                            </select>
                          </div>

                          <div className="col-lg-4 col-md-6">
                            <label className="form-label">Madurez</label>
                            <select className="form-select" name="madurez" value={formData.madurez || ''} onChange={handleInputChange}>
                              <option value="">Seleccionar madurez...</option>
                              <option value="poc">POC (Proof of Concept)</option>
                              <option value="piloto">Piloto</option>
                              <option value="proyecto">Proyecto</option>
                            </select>
                          </div>

                          <div className="col-lg-4 col-md-6">
                            <label className="form-label">% de avance</label>
                            <input type="number" min="0" max="100" className="form-control" name="porcentaje_avance"
                              value={formData.porcentaje_avance || ''} onChange={handleInputChange}
                              readOnly style={{ backgroundColor: '#f8f9fa' }} title="Campo calculado automáticamente" />
                          </div>

                          <div className="col-12">
                            <label className="form-label">Descripción</label>
                            <textarea className="form-control" rows="3" name="descripcion"
                              value={formData.descripcion} onChange={handleInputChange}></textarea>
                          </div>

                          <div className="col-12">
                            <label className="form-label">Relevancia para negocio</label>
                            <textarea className="form-control" rows="2" name="relevancia_para_negocio"
                              value={formData.relevancia_para_negocio || ''} onChange={handleInputChange}></textarea>
                          </div>

                          <div className="col-lg-12">
                            <label className="form-label">Iniciativa Padre (opcional)</label>
                            <Autocomplete
                              options={[
                                { id: '', nombre_de_la_oportunidad: 'Ninguna (Iniciativa principal)', tipo_de_iniciativa: '' },
                                ...iniciativas
                                  .filter(i => i.nombre_de_la_oportunidad && i.id !== parseInt(id))
                                  .sort((a, b) => {
                                    const nombreA = a.nombre_de_la_oportunidad || '';
                                    const nombreB = b.nombre_de_la_oportunidad || '';
                                    return nombreA.localeCompare(nombreB);
                                  })
                              ]}
                              getOptionLabel={(option) => {
                                if (!option.id) return option.nombre_de_la_oportunidad;
                                return `${option.nombre_de_la_oportunidad}${option.tipo_de_iniciativa ? ` [${option.tipo_de_iniciativa}]` : ''}`;
                              }}
                              value={iniciativas.find(i => i.id === formData.padre_id) || { id: '', nombre_de_la_oportunidad: 'Ninguna (Iniciativa principal)', tipo_de_iniciativa: '' }}
                              onChange={(event, newValue) => {
                                setFormData(prev => ({
                                  ...prev,
                                  padre_id: newValue ? newValue.id : ''
                                }));
                              }}
                              isOptionEqualToValue={(option, value) => option.id === value.id}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder="Buscar iniciativa..."
                                  variant="outlined"
                                  size="small"
                                />
                              )}
                              noOptionsText="No se encontraron iniciativas"
                              clearText="Limpiar"
                              openText="Abrir"
                              closeText="Cerrar"
                              sx={{ '& .MuiOutlinedInput-root': { padding: '4px' } }}
                            />
                            <div className="form-text">Si esta iniciativa depende de otra, selecciónela aquí. ({iniciativas.length} iniciativas disponibles)</div>
                          </div>

                          <div className="col-12 mt-2">
                            <h6 className="text-primary border-bottom pb-2 mb-3">
                              <i className="fas fa-building me-2"></i>Negocio y Técnica
                            </h6>
                          </div>

                          <div className="col-lg-8 col-md-6">
                            <label className="form-label">Estructura de negocio</label>
                            <select className="form-control" name="negocio_estructura" value={formData.negocio_estructura || ''} onChange={handleInputChange}>
                              <option value="">Seleccionar estructura...</option>
                              {Array.isArray(negocios) && negocios.map(negocio => (
                                <option key={negocio.id} value={negocio.id}>
                                  {negocio.linea_de_negocio} / {negocio.negocio} / {negocio.subnegocio}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div className="col-lg-4 col-md-6">
                            <label className="form-label">Contacto de negocio</label>
                            <input type="text" className="form-control" name="contacto_de_negocio"
                              value={formData.contacto_de_negocio || ''} onChange={handleInputChange} />
                          </div>

                          <div className="col-lg-6 col-md-6">
                            <label className="form-label">Área de conocimiento</label>
                            <select className="form-control" name="tecnologia" value={formData.tecnologia || ''} onChange={handleInputChange}>
                              <option value="">Seleccionar área...</option>
                              {Array.isArray(areas) && areas.map(area => (
                                <option key={area.id} value={area.id}>{area.nombre}</option>
                              ))}
                            </select>
                          </div>

                          <div className="col-lg-6 col-md-6">
                            <label className="form-label">Área de ejecución</label>
                            <select className="form-control" name="area_ejecucion" value={formData.area_ejecucion || ''} onChange={handleInputChange}>
                              <option value="">Seleccionar área...</option>
                              {Array.isArray(areas) && areas.map(area => (
                                <option key={area.id} value={area.id}>{area.nombre}</option>
                              ))}
                            </select>
                          </div>

                          <div className="col-12 mt-2">
                            <h6 className="text-primary border-bottom pb-2 mb-3">
                              <i className="fas fa-check-square me-2"></i>Opciones
                            </h6>
                          </div>

                          {[
                            { name: 'relevancia_para_el_dh', label: 'Relevancia para el DH' },
                            { name: 'revision_lt', label: 'Revisión LT' },
                            { name: 'revision_dn', label: 'En revisión de negocio' },
                            { name: 'productivizado', label: 'Productivizado' },
                            { name: 'innovacion', label: 'Innovación' },
                            { name: 'en_uso_escalado', label: 'En uso/escalado' },
                            { name: 'propuesta_enviada', label: 'Propuesta enviada' },
                          ].map(({ name, label }) => (
                            <div key={name} className="col-lg-3 col-md-6">
                              <div className="form-check form-switch">
                                <input className="form-check-input" type="checkbox" name={name}
                                  checked={formData[name]} onChange={handleInputChange} />
                                <label className="form-check-label">{label}</label>
                              </div>
                            </div>
                          ))}

                          <div className="col-lg-3 col-md-6">
                            <label className="form-label">Tipo de innovación</label>
                            <select className="form-select" name="tipo_innovacion" value={formData.tipo_innovacion || ''} onChange={handleInputChange}>
                              <option value="">Seleccionar tipo...</option>
                              <option value="innocard">Innocard</option>
                              <option value="innotrans">Innotrans</option>
                            </select>
                          </div>
                        </div>
                      )}

                      {/* ── ECONÓMICO ── */}
                      {activeSubTab === 'economico' && (
                        <div className="row g-3">
                          <div className="col-12">
                            <h6 className="text-primary border-bottom pb-2 mb-3">
                              <i className="fas fa-chart-bar me-2"></i>Estimaciones de Propuesta
                            </h6>
                          </div>

                          <div className="col-lg-3 col-md-6">
                            <label className="form-label">Valor mínimo estimado (€)</label>
                            <input type="number" step="0.01" className="form-control" name="valor_minimo_estimado"
                              value={formData.valor_minimo_estimado || ''} onChange={handleInputChange} />
                          </div>

                          <div className="col-lg-3 col-md-6">
                            <label className="form-label">Valor máximo estimado (€)</label>
                            <input type="number" step="0.01" className="form-control" name="valor_maximo_estimado"
                              value={formData.valor_maximo_estimado || ''} onChange={handleInputChange} />
                          </div>

                          <div className="col-lg-3 col-md-6">
                            <label className="form-label">Duración mínima estimada (días)</label>
                            <input type="number" className="form-control" name="duracion_minima_estimada"
                              value={formData.duracion_minima_estimada || ''} onChange={handleInputChange} />
                          </div>

                          <div className="col-lg-3 col-md-6">
                            <label className="form-label">Duración máxima estimada (días)</label>
                            <input type="number" className="form-control" name="duracion_maxima_estimada"
                              value={formData.duracion_maxima_estimada || ''} onChange={handleInputChange} />
                          </div>

                          <div className="col-lg-4 col-md-6">
                            <label className="form-label">Probabilidad de aprobación</label>
                            <select className="form-control" name="probabilidad_de_aprobacion" value={formData.probabilidad_de_aprobacion || ''} onChange={handleInputChange}>
                              <option value="">Seleccionar...</option>
                              <option value="alta">Alta</option>
                              <option value="media">Media</option>
                              <option value="baja">Baja</option>
                            </select>
                          </div>

                          <div className="col-12 mt-2">
                            <h6 className="text-primary border-bottom pb-2 mb-3">
                              <i className="fas fa-euro-sign me-2"></i>Datos Económicos
                            </h6>
                          </div>

                          <div className="col-lg-4 col-md-6">
                            <label className="form-label">Importe total estimado (€)</label>
                            <input type="number" step="0.01" className="form-control" name="importe_total_estimado"
                              value={formData.importe_total_estimado || ''} readOnly style={{ backgroundColor: '#f8f9fa' }} onChange={handleInputChange} />
                          </div>

                          <div className="col-lg-4 col-md-6">
                            <label className="form-label">Importe de Venta (€)</label>
                            <input type="number" step="0.01" className="form-control" name="importe_total_cliente"
                              value={formData.importe_total_cliente || ''} onChange={handleInputChange} />
                          </div>

                          <div className="col-lg-4 col-md-6">
                            <label className="form-label">Importe total real (€)</label>
                            <input type="number" step="0.01" className="form-control" name="importe_total_real"
                              readOnly style={{ backgroundColor: '#f8f9fa' }} value={formData.importe_total_real || ''} onChange={handleInputChange} />
                          </div>

                          <div className="col-12 mt-2">
                            <h6 className="text-primary border-bottom pb-2 mb-3">
                              <i className="fas fa-calculator me-2"></i>Costes Estimados
                            </h6>
                          </div>

                          <div className="col-lg-3 col-md-6">
                            <label className="form-label">Coste estimado desarrollo (€)</label>
                            <input type="number" step="0.01" className="form-control" name="coste_de_desarrollo_estimado"
                              value={formData.coste_de_desarrollo_estimado || ''} onChange={handleInputChange} />
                          </div>

                          <div className="col-lg-3 col-md-6">
                            <label className="form-label">Riesgos estimado (€)</label>
                            <input type="number" step="0.01" className="form-control" name="riesgos_estimado"
                              value={formData.riesgos_estimado || ''} onChange={handleInputChange} />
                          </div>

                          <div className="col-lg-3 col-md-6">
                            <label className="form-label">Estructura estimado (€)</label>
                            <input type="number" step="0.01" className="form-control" name="estructura_estimado"
                              value={formData.estructura_estimado || ''} onChange={handleInputChange} />
                          </div>

                          <div className="col-lg-3 col-md-6">
                            <label className="form-label">Gastos viajes estimado (€)</label>
                            <input type="number" step="0.01" className="form-control" name="coste_de_gastos_estimado"
                              value={formData.coste_de_gastos_estimado || ''} onChange={handleInputChange} />
                          </div>

                          <div className="col-lg-3 col-md-6">
                            <label className="form-label">Hardware estimado (€)</label>
                            <input type="number" step="0.01" className="form-control" name="hardware_estimado"
                              value={formData.hardware_estimado || ''} onChange={handleInputChange} />
                          </div>

                          <div className="col-lg-3 col-md-6">
                            <label className="form-label">OPEX estimado (€)</label>
                            <input type="number" step="0.01" className="form-control" name="opex_estimado"
                              value={formData.opex_estimado || ''} onChange={handleInputChange} />
                          </div>

                          <div className="col-lg-3 col-md-6">
                            <label className="form-label">Alquiler equipos estimado (€)</label>
                            <input type="number" step="0.01" className="form-control" name="coste_de_alquiler_de_equipos_estimado"
                              value={formData.coste_de_alquiler_de_equipos_estimado || ''} onChange={handleInputChange} />
                          </div>

                          <div className="col-lg-3 col-md-6">
                            <label className="form-label">Horas estimadas desarrollo</label>
                            <input type="number" step="0.01" className="form-control" name="horas_desarrollo_estimado"
                              value={formData.horas_desarrollo_estimado || ''} onChange={handleInputChange}
                              title="Horas de desarrollo según propuesta" />
                          </div>

                          <div className="col-12 mt-2">
                            <h6 className="text-primary border-bottom pb-2 mb-3">
                              <i className="fas fa-money-bill-wave me-2"></i>Costes Reales Detallados
                            </h6>
                          </div>

                          <div className="col-lg-4 col-md-6">
                            <label className="form-label">Coste desarrollo real (€)</label>
                            <input type="number" step="0.01" className="form-control" name="coste_de_desarrollo_real"
                              readOnly style={{ backgroundColor: '#f8f9fa' }} value={formData.coste_de_desarrollo_real || ''} onChange={handleInputChange} />
                          </div>

                          <div className="col-lg-4 col-md-6">
                            <label className="form-label">Gastos viajes real (€)</label>
                            <input type="number" step="0.01" className="form-control" name="coste_de_gastos_real"
                              value={formData.coste_de_gastos_real || ''} onChange={handleInputChange} />
                          </div>

                          <div className="col-lg-4 col-md-6">
                            <label className="form-label">Hardware y Licencias real (€)</label>
                            <input type="number" step="0.01" className="form-control" name="hardware_real"
                              value={formData.hardware_real || ''} onChange={handleInputChange} />
                          </div>

                          <div className="col-lg-4 col-md-6">
                            <label className="form-label">Costes mantenimiento real (€)</label>
                            <input type="number" step="0.01" className="form-control" name="opex_real"
                              value={formData.opex_real || ''} onChange={handleInputChange} />
                          </div>

                          <div className="col-lg-4 col-md-6">
                            <label className="form-label">Alquiler equipos real (€)</label>
                            <input type="number" step="0.01" className="form-control" name="coste_de_alquiler_de_equipos_real"
                              value={formData.coste_de_alquiler_de_equipos_real || ''} onChange={handleInputChange} />
                          </div>

                          <div className="col-lg-4 col-md-6">
                            <label className="form-label">Riesgos real (€)</label>
                            <input type="number" step="0.01" className="form-control" name="riesgos_real"
                              value={formData.riesgos_real || ''} onChange={handleInputChange} />
                          </div>

                          <div className="col-lg-4 col-md-6">
                            <label className="form-label">Estructura real (€)</label>
                            <input type="number" step="0.01" className="form-control" name="estructura_real"
                              value={formData.estructura_real || ''} onChange={handleInputChange} />
                          </div>
                        </div>
                      )}

                      {/* ── PLANIFICACIÓN ── */}
                      {activeSubTab === 'planificacion' && (
                        <div className="row g-3">
                          <div className="col-12">
                            <h6 className="text-primary border-bottom pb-2 mb-3">
                              <i className="fas fa-calendar me-2"></i>Fechas
                            </h6>
                          </div>

                          <div className="col-lg-4 col-md-6">
                            <label className="form-label">Año</label>
                            <input type="number" className="form-control" name="año"
                              value={formData.año || ''} onChange={handleInputChange}
                              min="2020" max="2030" placeholder="Ej: 2024" />
                          </div>

                          <div className="col-lg-4 col-md-6">
                            <label className="form-label">Fecha de entrega propuesta (prevista)</label>
                            <input type="date" className="form-control" name="fecha_de_entrega_propuesta_prevista"
                              value={formData.fecha_de_entrega_propuesta_prevista || ''} onChange={handleInputChange} />
                          </div>

                          <div className="col-lg-4 col-md-6">
                            <label className="form-label">Fecha de aprobación/propuesta firmada</label>
                            <input type="date" className="form-control" name="fecha_de_aprobacion_propuesta_firmada"
                              value={formData.fecha_de_aprobacion_propuesta_firmada || ''} onChange={handleInputChange} />
                          </div>

                          <div className="col-lg-4 col-md-6">
                            <label className="form-label">Fecha de inicio planificada</label>
                            <input type="date" className="form-control" name="fecha_de_inicio_planificada"
                              value={formData.fecha_de_inicio_planificada || ''} onChange={handleInputChange} />
                          </div>

                          <div className="col-lg-4 col-md-6">
                            <label className="form-label">Fecha de fin planificada</label>
                            <input type="date" className="form-control" name="fecha_de_fin_planificada"
                              value={formData.fecha_de_fin_planificada || ''} onChange={handleInputChange} />
                          </div>

                          <div className="col-lg-4 col-md-6">
                            <label className="form-label">Fecha de inicio real</label>
                            <input type="date" className="form-control" name="fecha_de_inicio_real"
                              value={formData.fecha_de_inicio_real || ''} onChange={handleInputChange} />
                          </div>

                          <div className="col-lg-4 col-md-6">
                            <label className="form-label">Fecha de fin real</label>
                            <input type="date" className="form-control" name="fecha_de_fin_real"
                              value={formData.fecha_de_fin_real || ''} onChange={handleInputChange} />
                          </div>

                          <div className="col-12 mt-2">
                            <h6 className="text-primary border-bottom pb-2 mb-3">
                              <i className="fas fa-chart-line me-2"></i>Análisis y Desviaciones
                              <small className="text-muted ms-2 fw-normal" style={{ fontSize: '0.75rem' }}>Campos calculados automáticamente</small>
                            </h6>
                          </div>

                          <div className="col-lg-3 col-md-6">
                            <label className="form-label">% Avance real</label>
                            <input type="number" min="0" max="100" className="form-control" name="porcentaje_avance"
                              value={formData.porcentaje_avance || ''} onChange={handleInputChange}
                              readOnly style={{ backgroundColor: '#f8f9fa' }} title="Campo calculado automáticamente" />
                          </div>

                          <div className="col-lg-3 col-md-6">
                            <label className="form-label">% Avance esperado</label>
                            <input type="number" step="0.01" min="0" max="100" className="form-control" name="porcentaje_avance_esperado"
                              value={formData.porcentaje_avance_esperado || ''} onChange={handleInputChange}
                              readOnly style={{ backgroundColor: '#f8f9fa' }} title="Campo calculado automáticamente" />
                          </div>

                          <div className="col-lg-3 col-md-6">
                            <label className="form-label">Desviación en planificación (%)</label>
                            <input type="number" step="0.01" className="form-control" name="desviacion_tiempo"
                              value={formData.desviacion_tiempo || ''} onChange={handleInputChange}
                              readOnly style={{ backgroundColor: '#f8f9fa' }} title="Campo calculado automáticamente" />
                          </div>

                          <div className="col-lg-3 col-md-6">
                            <label className="form-label">Coste esperado (€)</label>
                            <input type="number" step="0.01" className="form-control" name="coste_esperado"
                              value={formData.coste_esperado || ''} readOnly style={{ backgroundColor: '#f8f9fa' }} onChange={handleInputChange} />
                          </div>

                          <div className="col-lg-3 col-md-6">
                            <label className="form-label">Desviación en coste (€)</label>
                            <input type="number" step="0.01" className="form-control" name="desviacion_coste"
                              value={formData.desviacion_coste || ''} onChange={handleInputChange}
                              readOnly style={{ backgroundColor: '#f8f9fa' }} title="Campo calculado automáticamente" />
                          </div>
                        </div>
                      )}

                      {/* ── DOCUMENTOS ── */}
                      {activeSubTab === 'documentos' && (
                        <div className="row g-3">
                          <div className="col-12">
                            <h6 className="text-primary border-bottom pb-2 mb-3">
                              <i className="fas fa-link me-2"></i>Enlaces y Documentos
                            </h6>
                          </div>

                          <div className="col-lg-6 col-md-6">
                            <label className="form-label">Enlace al documento (PPT - Sharepoint)</label>
                            <input type="url" className="form-control" name="enlace_al_documento_ppt_sharepoint"
                              value={formData.enlace_al_documento_ppt_sharepoint || ''} onChange={handleInputChange} placeholder="https://..." />
                          </div>

                          <div className="col-lg-6 col-md-6">
                            <label className="form-label">Enlace a la página de Confluence</label>
                            <input type="url" className="form-control" name="enlace_a_la_pagina_de_confluence"
                              value={formData.enlace_a_la_pagina_de_confluence || ''} onChange={handleInputChange} placeholder="https://..." />
                          </div>

                          <div className="col-lg-6 col-md-6">
                            <label className="form-label">Enlace propuesta (PDF - Sharepoint)</label>
                            <input type="url" className="form-control" name="enlace_propuesta_pdf_sharepoint"
                              value={formData.enlace_propuesta_pdf_sharepoint || ''} onChange={handleInputChange} placeholder="https://..." />
                          </div>

                          <div className="col-lg-12 col-md-12">
                            <label className="form-label">Motivo tipificado</label>
                            <input type="text" className="form-control" name="motivo_tipificado"
                              value={formData.motivo_tipificado || ''} onChange={handleInputChange} />
                          </div>
                        </div>
                      )}

                      {/* Botones de acción */}
                      <div className="row mt-4">
                        <div className="col-12">
                          <hr className="mt-2" />
                          <div className="d-flex justify-content-between">
                            <button
                              type="button"
                              className="btn btn-outline-secondary"
                              onClick={handleCancel}
                              disabled={loading}
                            >
                              <i className="fas fa-times me-2"></i>
                              Cancelar
                            </button>

                            <button
                              type="submit"
                              className="btn btn-primary btn-lg"
                              disabled={loading}
                            >
                              {loading ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-2"></span>
                                  Guardando...
                                </>
                              ) : (
                                <>
                                  <i className="fas fa-save me-2"></i>
                                  Guardar Cambios
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>

                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Tab de Personas */}
            {activeTab === 'personas' && (
              <div className="card">
                <div className="card-header">
                  <h5 className="card-title mb-0">
                    <i className="fas fa-users me-2"></i>
                    Personas Vinculadas
                  </h5>
                </div>
                <div className="card-body">
                  {/* Lista de personas vinculadas */}
                  {personasVinculadas.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table table-striped">
                        <thead>
                          <tr>
                            <th>Nombre</th>
                            <th>Rol</th>
                            <th>Email</th>
                            <th>Área</th>
                            <th>Estado</th>
                            <th>Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {personasVinculadas.map((personaVinculada) => (
                            <tr key={personaVinculada.id}>
                              <td>
                                <strong>{personaVinculada.persona_nombre}</strong>
                              </td>
                              <td>
                                <span className="badge bg-secondary">
                                  {getRolDisplay(personaVinculada.rol)}
                                </span>
                              </td>
                              <td>{personaVinculada.persona_email || '-'}</td>
                              <td>{personaVinculada.persona_area || '-'}</td>
                              <td>
                                <span className={`badge ${personaVinculada.activo ? 'bg-success' : 'bg-secondary'}`}>
                                  {personaVinculada.activo ? 'Activo' : 'Inactivo'}
                                </span>
                              </td>
                              <td>
                                <button
                                  className="btn btn-danger btn-sm"
                                  onClick={() => handleEliminarPersona(personaVinculada.id)}
                                  title="Eliminar persona"
                                >
                                  <i className="fas fa-times me-1"></i>
                                  Eliminar
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <i className="fas fa-users fa-3x text-muted mb-3"></i>
                      <p className="text-muted">No hay personas vinculadas a esta iniciativa</p>
                    </div>
                  )}

                  {/* Formulario para agregar nueva persona */}
                  <div className="mt-4 pt-4 border-top">
                    <h6 className="mb-3">Agregar Nueva Persona</h6>
                    <div className="row g-3">
                      <div className="col-md-4">
                        <label htmlFor="select-persona-edit" className="form-label small">
                          Persona
                        </label>
                        <select
                          id="select-persona-edit"
                          className="form-control"
                          value={selectedPersona}
                          onChange={(e) => setSelectedPersona(e.target.value)}
                        >
                          <option value="">Seleccionar persona</option>
                          {Array.isArray(personas) && personas.map(persona => (
                            <option key={persona.id} value={persona.id}>
                              {persona.nombre} {persona.apellido}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="col-md-4">
                        <label htmlFor="select-rol-edit" className="form-label small">
                          Rol
                        </label>
                        <select
                          id="select-rol-edit"
                          className="form-control"
                          value={selectedRol}
                          onChange={(e) => setSelectedRol(e.target.value)}
                        >
                          <option value="">Seleccionar rol</option>
                          <option value="jefe_proyecto">Jefe de Proyecto</option>
                          <option value="lider_tecnico">Líder Técnico</option>
                          <option value="desarrollador">Desarrollador</option>
                          <option value="responsable_comercial">Responsable DN</option>
                          <option value="colaborador">Colaborador</option>
                        </select>
                      </div>

                      <div className="col-md-4 d-flex align-items-end">
                        <button
                          type="button"
                          className="btn btn-success w-100"
                          onClick={addPersonaToList}
                          disabled={!selectedPersona || !selectedRol}
                          style={{ padding: '0.5rem' }}
                        >
                          <i className="fas fa-plus me-1"></i>
                          Añadir
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Tab de Hitos de Facturación */}
            {activeTab === 'hitos' && (
              <div className="card">
                <div className="card-header">
                  <h5 className="card-title mb-0">
                    <i className="fas fa-euro-sign me-2"></i>
                    Hitos de Facturación
                  </h5>
                </div>
                <div className="card-body">
                  {/* Lista de hitos */}
                  {hitosFacturacion.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table table-striped">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Nombre</th>
                            <th>Fecha</th>
                            <th>%</th>
                            <th>Importe</th>
                            <th>Cliente</th>
                            <th>Sociedad Final</th>
                            <th>Facturado</th>
                            <th>Zona Geográfica</th>
                            <th>País</th>
                            <th>Observaciones</th>
                            <th>Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {hitosFacturacion.map((hito) => (
                            <tr key={hito.id}>
                              <td><strong>{hito.numero_hito}</strong></td>
                              <td>{hito.nombre}</td>
                              <td>{hito.fecha || '-'}</td>
                              <td>{hito.porcentaje ? `${hito.porcentaje}%` : '-'}</td>
                              <td>{hito.importe ? `${parseFloat(hito.importe).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €` : '-'}</td>
                              <td>{hito.cliente || '-'}</td>
                              <td>{hito.sociedad_final || '-'}</td>
                              <td>
                                <span className={`badge ${hito.facturado ? 'bg-success' : 'bg-secondary'}`}>
                                  {hito.facturado ? 'Sí' : 'No'}
                                </span>
                              </td>
                              <td>{hito.zona_geografica_display || hito.zona_geografica || '-'}</td>
                              <td>{hito.pais_display || hito.pais || '-'}</td>
                              <td>{hito.observaciones || '-'}</td>
                              <td style={{ whiteSpace: 'nowrap' }}>
                                <button
                                  className="btn btn-sm btn-outline-dark"
                                  title="Editar"
                                  onClick={() => handleAbrirHitoModal(hito)}
                                >
                                  <i className="fas fa-edit"></i>
                                </button>

                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <i className="fas fa-euro-sign fa-3x text-muted mb-3"></i>
                      <p className="text-muted">No hay hitos de facturación configurados</p>
                    </div>
                  )}

                  {/* Formulario para agregar nuevo hito */}
                  <div className="mt-4 pt-4 border-top">
                    <h6 className="mb-3">Agregar Nuevo Hito</h6>
                    {nuevoHito.sociedad_final === '' && formData.negocio_estructura && (() => {
                      const neg = negocios.find(n => n.id === Number(formData.negocio_estructura));
                      if (neg?.nombre_sociedad_final && nuevoHito.sociedad_final !== neg.nombre_sociedad_final) {
                        setTimeout(() => setNuevoHito(prev => ({ ...prev, sociedad_final: neg.nombre_sociedad_final })), 0);
                      }
                      return null;
                    })()}
                    <div className="row g-3">
                      <div className="col-md-2">
                        <label className="form-label">Número</label>
                        <input
                          type="number"
                          className="form-control"
                          value={nuevoHito.numero_hito || ''}
                          onChange={(e) => setNuevoHito({ ...nuevoHito, numero_hito: e.target.value })}
                          placeholder="1, 2, 3..."
                        />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Nombre</label>
                        <input
                          type="text"
                          className="form-control"
                          value={nuevoHito.nombre || ''}
                          onChange={(e) => setNuevoHito({ ...nuevoHito, nombre: e.target.value })}
                          placeholder="Ej: Primer entregable"
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">Fecha</label>
                        <input
                          type="date"
                          className="form-control"
                          value={nuevoHito.fecha || ''}
                          onChange={(e) => setNuevoHito({ ...nuevoHito, fecha: e.target.value })}
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">Cliente</label>
                        <select
                          className="form-control"
                          value={nuevoHito.cliente || ''}
                          onChange={(e) => setNuevoHito({ ...nuevoHito, cliente: e.target.value })}
                        >
                          <option value="">— Sin cliente —</option>
                          {subnegocioOptions.map(s => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-2">
                        <label className="form-label">Porcentaje (%)</label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          className="form-control"
                          value={nuevoHito.porcentaje || ''}
                          onChange={(e) => setNuevoHito({ ...nuevoHito, porcentaje: e.target.value })}
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">Importe (€)</label>
                        <input
                          type="number"
                          step="0.01"
                          className="form-control"
                          value={nuevoHito.importe || ''}
                          onChange={(e) => setNuevoHito({ ...nuevoHito, importe: e.target.value })}
                        />
                        {formData.importe_total_cliente && (
                          <small className="text-muted">
                            Precio de venta: {parseFloat(formData.importe_total_cliente).toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}
                          </small>
                        )}
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">Fecha facturación</label>
                        <input
                          type="date"
                          className="form-control"
                          value={nuevoHito.fecha_facturacion || ''}
                          onChange={(e) => setNuevoHito({ ...nuevoHito, fecha_facturacion: e.target.value })}
                        />
                      </div>
                      <div className="col-md-2">
                        <label className="form-label">Facturado</label>
                        <div className="form-check form-switch mt-2">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={nuevoHito.facturado}
                            onChange={(e) => setNuevoHito({ ...nuevoHito, facturado: e.target.checked })}
                          />
                        </div>
                      </div>
                      <div className="col-md-12">
                        <label className="form-label">Sociedad Final</label>
                        <input
                          type="text"
                          className="form-control"
                          value={nuevoHito.sociedad_final || ''}
                          onChange={(e) => setNuevoHito({ ...nuevoHito, sociedad_final: e.target.value })}
                          placeholder="Rellenado automáticamente. Puede modificarlo."
                        />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Zona Geográfica</label>
                        <select
                          className="form-control"
                          value={nuevoHito.zona_geografica || ''}
                          onChange={(e) => setNuevoHito({ ...nuevoHito, zona_geografica: e.target.value })}
                        >
                          <option value="">— Sin zona —</option>
                          {zonaGeograficaOptions.map(({ value, label }) => (
                            <option key={value} value={value}>{label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-8">
                        <label className="form-label">País</label>
                        <select
                          className="form-control"
                          value={nuevoHito.pais || ''}
                          onChange={(e) => setNuevoHito({ ...nuevoHito, pais: e.target.value })}
                        >
                          <option value="">— Sin país —</option>
                          {paisOptions.map(({ value, label }) => (
                            <option key={value} value={value}>{label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-12">
                        <label className="form-label">Observaciones</label>
                        <textarea
                          className="form-control"
                          rows="2"
                          value={nuevoHito.observaciones || ''}
                          onChange={(e) => setNuevoHito({ ...nuevoHito, observaciones: e.target.value })}
                          placeholder="Observaciones adicionales..."
                        />
                      </div>
                      <div className="col-md-12">
                        <button
                          className="btn btn-success"
                          onClick={handleAgregarHito}
                          disabled={!nuevoHito.nombre || !nuevoHito.numero_hito}
                        >
                          <i className="fas fa-plus me-2"></i>
                          Agregar Hito
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Tab de Comentarios */}
            {activeTab === 'comentarios' && (
              <div className="card">
                <div className="card-header">
                  <h5 className="card-title mb-0">
                    <i className="fas fa-comments me-2"></i>
                    Comentarios
                  </h5>
                </div>
                <div className="card-body">
                  {/* Lista de comentarios */}
                  {comentarios.length > 0 ? (
                    <div className="mb-4">
                      {comentarios.map((comentario) => (
                        <div key={comentario.id} className="card mb-3">
                          <div className="card-body" style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                            {/* Contenido del comentario */}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <small className="text-muted d-block mb-2">
                                {comentario.autor_nombre && (
                                  <strong style={{ color: '#2c3e50', marginRight: '6px' }}>{comentario.autor_nombre}</strong>
                                )}
                                <i className="fas fa-calendar me-1"></i>
                                {new Date(comentario.fecha_creacion || comentario.created_at).toLocaleString('es-ES')}
                                {comentario.fecha_modificacion && comentario.fecha_modificacion !== comentario.fecha_creacion && (
                                  <span> · Modificado: {new Date(comentario.fecha_modificacion).toLocaleString('es-ES')}</span>
                                )}
                              </small>
                              {editingComentarioId === comentario.id ? (
                                <div>
                                  <textarea
                                    className="form-control mb-2"
                                    rows="3"
                                    value={editingComentarioTexto}
                                    onChange={(e) => setEditingComentarioTexto(e.target.value)}
                                  />
                                  <div className="d-flex gap-2">
                                    <button
                                      className="btn btn-sm btn-primary"
                                      onClick={() => handleGuardarEdicionComentario(comentario.id)}
                                      disabled={!editingComentarioTexto.trim()}
                                    >
                                      Guardar
                                    </button>
                                    <button
                                      className="btn btn-sm btn-secondary"
                                      onClick={() => { setEditingComentarioId(null); setEditingComentarioTexto(''); }}
                                    >
                                      Cancelar
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                                  {comentario.contenido}
                                </p>
                              )}
                            </div>
                            {/* Acciones: solo para el autor */}
                            {comentario.autor_nombre === currentUserName && editingComentarioId !== comentario.id && (
                              <div style={{ display: 'flex', flexDirection: 'row', gap: '4px', flexShrink: 0, alignSelf: 'center' }}>
                                <button
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => { setEditingComentarioId(comentario.id); setEditingComentarioTexto(comentario.contenido); }}
                                  title="Editar comentario"
                                  style={{ padding: '3px 8px' }}
                                >
                                  <i className="fas fa-edit"></i>
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => handleEliminarComentario(comentario.id)}
                                  title="Eliminar comentario"
                                  style={{ padding: '3px 8px' }}
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4 mb-4">
                      <i className="fas fa-comments fa-3x text-muted mb-3"></i>
                      <p className="text-muted">No hay comentarios en esta iniciativa</p>
                    </div>
                  )}

                  {/* Formulario para agregar nuevo comentario */}
                  <div className="border-top pt-4">
                    <h6 className="mb-3">Agregar Comentario</h6>
                    <div className="mb-3">
                      <textarea
                        className="form-control"
                        rows="4"
                        value={nuevoComentario || ''}
                        onChange={(e) => setNuevoComentario(e.target.value)}
                        placeholder="Escriba su comentario aquí..."
                      />
                    </div>
                    <button
                      className="btn btn-primary"
                      onClick={handleAgregarComentario}
                      disabled={!nuevoComentario.trim()}
                    >
                      <i className="fas fa-comment me-2"></i>
                      Agregar Comentario
                    </button>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    <FechaEntregaRealModal
      open={fechaEntregaRealModalOpen}
      onClose={handleFechaEntregaRealClose}
      onConfirm={handleFechaEntregaRealConfirm}
      initialDate={new Date().toISOString().split('T')[0]}
    />
    <EnlacePropuestaModal
      open={enlaceModalOpen}
      onClose={handleEnlaceClose}
      onConfirm={handleEnlaceConfirm}
      iniciativaId={id}
      enlaceActual={enlaceActual}
      estadoDestino={formData.estado}
    />
    <SupervisionModal
      open={supervisionModalOpen}
      onClose={handleSupervisionClose}
      onConfirm={handleSupervisionConfirm}
      iniciativaId={id}
      supervisionStatus={supervisionStatus}
      loading={supervisionLoading}
    />
    <MotivoRechazoModal
      open={motivoRechazoModalOpen}
      onClose={handleMotivoRechazoClose}
      onConfirm={handleMotivoRechazoConfirm}
      motivoActual={formData.motivo_tipificado || ''}
    />
    <AprobadaModal
      open={aprobadaModalOpen}
      onClose={handleAprobadaClose}
      onConfirm={handleAprobadaConfirm}
      iniciativa={iniciativa}
      loading={loading}
    />
    <HitoModal
      open={hitoModalOpen}
      onClose={handleCerrarHitoModal}
      hito={hitoSeleccionado}
      onSave={handleGuardarHitoModal}
      iniciativas={iniciativa ? [{ id: iniciativa.id, nombre_de_la_oportunidad: iniciativa.nombre_de_la_oportunidad, nombre_sociedad_final: negocios.find(n => n.id === Number(formData.negocio_estructura))?.nombre_sociedad_final || '', importe_total_cliente: formData.importe_total_cliente || null }, ...iniciativas] : iniciativas}
      defaultIniciativaId={iniciativa?.id}
    />
    </div>
  );
};

export default EditarIniciativa;